import React from "react";

/** Accept only local destinations. Never turn sign-in into an open redirect. */
export function authenticationDestination(value: string | null | undefined): string {
  if (!value || !value.startsWith("/") || /[\\\u0000-\u0020]/.test(value)) return "/";
  try {
    const url = new URL(value, "https://excellent.invalid");
    const decodedPath = decodeURIComponent(url.pathname);
    if (
      url.origin !== "https://excellent.invalid" ||
      /[\\\u0000-\u0020]/.test(decodedPath) ||
      decodedPath.startsWith("//") ||
      /^\/login(?:\/|$)/i.test(decodedPath) ||
      decodedPath.startsWith("/api/")
    )
      return "/";
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "/";
  }
}

export function authenticationHref(destination: string): string {
  return `/login?returnTo=${encodeURIComponent(authenticationDestination(destination))}`;
}

type Observation = { data?: unknown; error?: unknown; isValidating?: boolean };
export type AuthenticationConnection = "checking" | "sign-in" | "unavailable" | "ready";

function isUnauthorized(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error && error.status === 401;
}

/** Failed refreshes override cached success. A 403 is not an expired session. */
export function authenticationConnection(
  auth: Observation & { data?: { authenticated: boolean } },
  shell: Observation,
  networkAvailable = true,
): AuthenticationConnection {
  if (isUnauthorized(auth.error) || isUnauthorized(shell.error)) return "sign-in";
  if (!auth.error && auth.data?.authenticated === false) return "sign-in";
  if (!networkAvailable || auth.error || shell.error) return "unavailable";
  if (!auth.data || !shell.data) return "checking";
  return auth.data.authenticated ? "ready" : "sign-in";
}

export const authenticationConnectionLabel: Record<AuthenticationConnection, string> = {
  checking: "Checking connection…",
  "sign-in": "Sign in required",
  unavailable: "Connection unavailable",
  ready: "Workspace reachable",
};

export function observedReadiness<T>(
  observation: {
    data?: { agents: T[] };
    error?: unknown;
    isValidating?: boolean;
  },
  connection: AuthenticationConnection = "ready",
): T[] | undefined {
  return connection !== "ready" || observation.error || observation.isValidating
    ? undefined
    : observation.data?.agents;
}

export function AuthenticationState({
  state,
  destination,
  onRetry,
  retrying = false,
}: {
  state: AuthenticationConnection;
  destination: string;
  onRetry: () => void;
  retrying?: boolean;
}) {
  if (state === "ready" || state === "checking") return null;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-border bg-background px-4 py-3 text-sm text-foreground"
      data-authentication-state={state}
    >
      {state === "sign-in" ? (
        <>
          <div className="min-w-0">
            <p className="font-medium">Sign in to continue</p>
            <p className="mt-0.5 text-muted-foreground">
              Your workspace could not be loaded. Sign in to return to this page.
            </p>
          </div>
          <a
            className="inline-flex min-h-9 shrink-0 items-center justify-center rounded-md bg-primary px-4 font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            href={authenticationHref(destination)}
          >
            Sign in
          </a>
        </>
      ) : (
        <>
          <div className="min-w-0">
            <p className="font-medium">Workspace access unavailable</p>
            <p className="mt-0.5 text-muted-foreground">
              {retrying
                ? "Checking your connection and account access…"
                : "Check your connection or account access, then try again."}
            </p>
          </div>
          <button
            type="button"
            className="inline-flex min-h-9 shrink-0 items-center justify-center rounded-md border border-border bg-background px-4 font-medium hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-60"
            onClick={onRetry}
            disabled={retrying}
          >
            {retrying ? "Checking connection…" : "Retry connection"}
          </button>
        </>
      )}
    </div>
  );
}
